import React, { useEffect, useState, useRef } from 'react';
import { ImageOff, RotateCcw } from 'lucide-react';
import {
  fetchProtectedBlobWithResponse,
  getCachedObjectUrl,
} from '../utils/protectedMedia';

export const CircularProgress = ({
  progress = null, // 0..100 or null for indeterminate spinning
  size = 46,
  strokeWidth = 3.5,
  className = '',
  trackColor = 'text-white/20',
  indicatorColor = 'text-blue-500',
  showPercent = true,
  label = null,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const isDeterminate = typeof progress === 'number' && progress >= 0;
  const clampedProgress = isDeterminate ? Math.min(100, Math.max(0, progress)) : 0;
  const offset = isDeterminate
    ? circumference - (clampedProgress / 100) * circumference
    : 0;

  return (
    <div className={`inline-flex flex-col items-center justify-center gap-1.5 ${className}`}>
      <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
        <svg
          className={`h-full w-full ${!isDeterminate ? 'animate-spin' : '-rotate-90 transform'}`}
          viewBox={`0 0 ${size} ${size}`}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className={`${trackColor} fill-none`}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeDasharray={isDeterminate ? circumference : `${circumference * 0.28} ${circumference * 0.72}`}
            strokeDashoffset={isDeterminate ? offset : undefined}
            strokeLinecap="round"
            className={`${indicatorColor} fill-none transition-all duration-150 ease-out`}
          />
        </svg>
        {isDeterminate && showPercent && size >= 34 ? (
          <span className="absolute text-[11px] font-bold text-white drop-shadow">
            {Math.round(clampedProgress)}%
          </span>
        ) : null}
      </div>
      {label ? (
        <span className="rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white/90 backdrop-blur-sm select-none">
          {label}
        </span>
      ) : null}
    </div>
  );
};

const ProtectedImage = ({
  src,
  thumbnailSrc,
  alt,
  className = '',
  style,
  showLoader = false,
  loaderText = null,
  onLoaded,
  ...props
}) => {
  const cachedInitial = getCachedObjectUrl(src);
  const cachedThumbInitial = thumbnailSrc ? getCachedObjectUrl(thumbnailSrc) : null;

  const [objectUrl, setObjectUrl] = useState(cachedInitial || '');
  const [thumbUrl, setThumbUrl] = useState(cachedThumbInitial || '');
  const [loading, setLoading] = useState(!cachedInitial);
  const [progress, setProgress] = useState(cachedInitial ? 100 : null);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  const activeSrcRef = useRef(src);
  activeSrcRef.current = src;

  // 1. Загрузка миниатюры (если передана для плавного превью пока грузится оригинал)
  useEffect(() => {
    if (!thumbnailSrc || cachedInitial) return;

    const cachedThumb = getCachedObjectUrl(thumbnailSrc);
    if (cachedThumb) {
      setThumbUrl(cachedThumb);
      return;
    }

    let cancelled = false;
    fetchProtectedBlobWithResponse(thumbnailSrc)
      .then(({ objectUrl: tUrl }) => {
        if (!cancelled && tUrl) {
          setThumbUrl(tUrl);
        }
      })
      .catch(() => { /* игнорируем ошибку превью */ });

    return () => {
      cancelled = true;
    };
  }, [thumbnailSrc, cachedInitial]);

  // 2. Загрузка основного изображения
  useEffect(() => {
    if (!src) {
      setObjectUrl('');
      setLoading(false);
      setProgress(null);
      setError(null);
      return;
    }

    const cached = getCachedObjectUrl(src);
    if (cached) {
      setObjectUrl(cached);
      setLoading(false);
      setProgress(100);
      setError(null);
      onLoaded?.();
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setProgress(null);

    fetchProtectedBlobWithResponse(src, (percent) => {
      if (!cancelled) {
        setProgress(percent);
      }
    })
      .then(({ objectUrl: newUrl }) => {
        if (cancelled) return;
        setObjectUrl(newUrl);
        setLoading(false);
        setProgress(100);
        onLoaded?.();
      })
      .catch((err) => {
        if (cancelled) return;
        setLoading(false);
        setError(err?.message || 'Не удалось загрузить изображение');
      });

    return () => {
      cancelled = true;
    };
  }, [src, retryKey]);

  // Ошибка загрузки
  if (error && !objectUrl && !thumbUrl) {
    return (
      <div className={`flex flex-col items-center justify-center gap-2 rounded-lg bg-gray-100 p-3 text-center text-gray-500 dark:bg-gray-800 dark:text-gray-400 ${className}`}>
        <ImageOff className="h-6 w-6 opacity-60" />
        <span className="text-[11px] leading-tight">Ошибка загрузки</span>
        <button
          type="button"
          onClick={() => setRetryKey((k) => k + 1)}
          className="inline-flex items-center gap-1 rounded bg-white/80 px-2 py-0.5 text-[10px] font-medium text-gray-700 shadow-sm hover:bg-white dark:bg-gray-700 dark:text-gray-200"
        >
          <RotateCcw className="h-3 w-3" />
          Повторить
        </button>
      </div>
    );
  }

  // Если передан thumbnailSrc или явно запрошен loader (например, в полноэкранном просмотре):
  if (thumbnailSrc || showLoader) {
    const displayThumb = thumbUrl && !objectUrl;
    const isDownloadingFull = loading && !objectUrl;

    return (
      <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
        {/* Размытая миниатюра-заглушка на время загрузки полного файла */}
        {displayThumb && (
          <img
            src={thumbUrl}
            alt={alt}
            className={`pointer-events-none absolute inset-0 h-full w-full object-contain filter blur-md brightness-75 transition-opacity duration-300 ${className}`}
            style={style}
            draggable={false}
          />
        )}

        {/* Индикатор загрузки с круговым прогресс-баром по центру */}
        {isDownloadingFull && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/30 backdrop-blur-[2px]">
            <CircularProgress
              progress={progress}
              size={54}
              strokeWidth={4}
              indicatorColor="text-white"
              trackColor="text-white/20"
              label={loaderText || (progress !== null ? `Загрузка оригинала ${progress}%` : 'Загрузка фото...')}
            />
          </div>
        )}

        {/* Основное полноразмерное изображение */}
        {objectUrl && (
          <img
            src={objectUrl}
            alt={alt}
            className={`transition-opacity duration-200 ${loading ? 'opacity-0' : 'opacity-100'} ${className}`}
            style={style}
            {...props}
          />
        )}
      </div>
    );
  }

  // Стандартный рендер (для компактных миниатюр в списках)
  return (
    <div className="relative h-full w-full">
      {loading && !objectUrl && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200/70 dark:bg-gray-800/70">
          <CircularProgress size={24} strokeWidth={2.5} indicatorColor="text-blue-500" trackColor="text-gray-300 dark:text-gray-700" showPercent={false} />
        </div>
      )}
      <img
        src={objectUrl || undefined}
        alt={alt}
        className={`${loading && !objectUrl ? 'opacity-0' : 'opacity-100'} transition-opacity duration-150 ${className}`}
        style={style}
        {...props}
      />
    </div>
  );
};

export default ProtectedImage;
